/*
 * Carbisdale - TextProcessor
 *
 * Zentrale Pure Functions fuer Text-Processing, Format-Erkennung,
 * Display-Mapping und Marker-Handling. Wird von EditorView/EditorInput/EditorModel
 * und CoreView/CoreInput/CoreModel gemeinsam genutzt.
 *
 * Alle Funktionen sind zustandslos (Pure Functions) — sie lesen und schreiben
 * keinen externen State, sondern arbeiten nur mit ihren Parametern.
 */

import { Style } from './config.js'

// Vorkompilierte Unicode-Regex für Wort-Erkennung (zentral, einmal allokiert)
const WORD_CHAR_RE = /[\p{L}\p{N}_]/u
const LETTER_DIGIT_RE = /[\p{L}\p{N}]/u

/** Prüft ob ein Zeichen ein Wort-Zeichen ist (Buchstabe, Ziffer, Unterstrich). */
export function isWordChar(ch) { return WORD_CHAR_RE.test(ch) }

/** Prüft ob ein Zeichen ein Buchstabe oder eine Ziffer ist (ohne Unterstrich). */
export function isLetterOrDigit(ch) { return LETTER_DIGIT_RE.test(ch) }

// === Codepoint / Surrogate-Pair Hilfsfunktionen ===

// Regex für BMP-Emoji mit Emoji-Präsentation (☕, ✅, ⭐ etc.)
const EMOJI_PRESENTATION_RE = /\p{Emoji_Presentation}/u

/**
 * Gibt die Display-Breite eines Zeichens in Monospace-Spalten zurück.
 * Emojis belegen 2 Spalten wie in echten Terminals — sowohl Supplementary-Plane-
 * Zeichen (Surrogate Pairs, ch.length > 1) als auch BMP-Emoji mit Emoji_Presentation.
 * @param {string} ch - Ein einzelnes Zeichen (ggf. Surrogate Pair)
 * @returns {number} 1 oder 2
 */
export function charDisplayWidth(ch) {
    if (ch.length > 1) return 2  // Surrogate Pairs (Supplementary Plane)
    return EMOJI_PRESENTATION_RE.test(ch) ? 2 : 1
}

/**
 * Gibt die UTF-16-Länge des Codepoints an Position offset zurück (1 oder 2).
 * Bei Surrogate Pairs (Emojis etc.) ist die Länge 2.
 * @param {string} text
 * @param {number} offset - UTF-16 Position
 * @returns {number} 1 oder 2
 */
export function codepointLengthAt(text, offset) {
    if (offset < 0 || offset >= text.length) return 1
    const code = text.charCodeAt(offset)
    // High Surrogate (0xD800–0xDBFF) → Surrogate Pair mit 2 Code Units
    if (code >= 0xD800 && code <= 0xDBFF && offset + 1 < text.length) return 2
    return 1
}

/**
 * Gibt die UTF-16-Länge des Codepoints VOR Position offset zurück (1 oder 2).
 * @param {string} text
 * @param {number} offset - UTF-16 Position
 * @returns {number} 1 oder 2
 */
export function codepointLengthBefore(text, offset) {
    if (offset <= 0) return 1
    const code = text.charCodeAt(offset - 1)
    // Low Surrogate (0xDC00–0xDFFF) → vorherige Code Unit war High Surrogate
    if (code >= 0xDC00 && code <= 0xDFFF && offset >= 2) return 2
    return 1
}

/**
 * Korrigiert einen Offset, der mitten in einem Surrogate Pair landet.
 * Rundet nach links (zum Anfang des Codepoints).
 * @param {string} text
 * @param {number} offset
 * @returns {number} Gültiger Codepoint-Boundary-Offset
 */
export function snapToCodepointBoundary(text, offset) {
    if (offset <= 0 || offset >= text.length) return offset
    const code = text.charCodeAt(offset)
    // Wenn wir auf einem Low Surrogate stehen, eins nach links
    if (code >= 0xDC00 && code <= 0xDFFF) return offset - 1
    return offset
}

/**
 * Sammelt alle Marker-Positionen aus Format-Spans in ein Set.
 * @param {Array<{start: number, end: number, markerLen: number}>} spans
 * @returns {Set<number>}
 */
export function collectMarkerPositions(spans) {
    const positions = new Set()
    for (const span of spans) {
        for (let i = span.start; i < span.start + span.markerLen; i++) positions.add(i)
        for (let i = span.end - span.markerLen; i < span.end; i++) positions.add(i)
    }
    return positions
}

// Regex-Patterns als Modul-Konstanten (vermeidet Neucompilierung bei jedem Aufruf)
const BOLD_REGEX = /(?:^|(?<=[\s(\[*~_]))\*\*(.+?)\*\*(?=$|[\s.,!?;:'")\]\-*~_])/g
const STRIKE_REGEX = /(?:^|(?<=[\s(\[*~_]))~~(.+?)~~(?=$|[\s.,!?;:'")\]\-*~_])/g
const ITALIC_REGEX = /(?:^|(?<=[\s(\[*~_]))(?<!\*)\*(.+?)\*(?!\*)(?=$|[\s.,!?;:'")\]\-*~_])/g
const UNDERLINE_REGEX = /(?:^|(?<=[\s(\[*~_]))(?<!_)_(.+?)_(?!_)(?=$|[\s.,!?;:'")\]\-*~_])/g
const CODE_REGEX = /`([^`]+)`/g

// ============================================================================
// Kategorie A: 1:1 extrahierte Funktionen (keine Logik-Aenderung)
// ============================================================================

/**
 * Findet alle gueltigen Format-Spans im Text.
 * Erkennt Bold (**), Italic (*), Strikethrough (~~), Underline (_) und Bold-Italic (***).
 * @param {string} text
 * @returns {Array<{start: number, end: number, type: string, markerLen: number}>}
 */
export function findFormatSpans(text) {
    const spans = []
    let match

    // Inline-Code: `text` — vor allen anderen, damit Code-Bereiche geschützt sind
    CODE_REGEX.lastIndex = 0
    while ((match = CODE_REGEX.exec(text)) !== null) {
        spans.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'code',
            markerLen: 1
        })
    }

    // Hilfsfunktion: liegt ein Match innerhalb eines Code-Spans?
    const isInsideCode = (idx, len) => spans.some(s =>
        s.type === 'code' && idx >= s.start && idx + len <= s.end)

    // Bold: **text** (relaxiert: .+? statt \S.*?\S|\S fuer WYSIWYG-Kompatibilitaet)
    BOLD_REGEX.lastIndex = 0
    while ((match = BOLD_REGEX.exec(text)) !== null) {
        if (isInsideCode(match.index, match[0].length)) continue
        spans.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'bold',
            markerLen: 2
        })
    }

    // Strikethrough: ~~text~~ (relaxiert fuer WYSIWYG)
    STRIKE_REGEX.lastIndex = 0
    while ((match = STRIKE_REGEX.exec(text)) !== null) {
        if (isInsideCode(match.index, match[0].length)) continue
        spans.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'strikethrough',
            markerLen: 2
        })
    }

    // Bold-Italic: ***text*** — Bold-Regex matcht ***text** (non-greedy),
    // der letzte * bleibt uebrig. Erkennung: Content beginnt mit * UND
    // direkt nach dem Bold-Span steht ein weiterer *.
    for (const span of spans) {
        if (span.type !== 'bold') continue
        const content = text.substring(span.start + 2, span.end - 2)
        if (content.length >= 2
            && content[0] === '*' && content[1] !== '*'
            && span.end < text.length && text[span.end] === '*'
            && (span.end + 1 >= text.length || text[span.end + 1] !== '*')) {
            span.end += 1
            spans.push({
                start: span.start + 2,
                end: span.end - 2,
                type: 'italic',
                markerLen: 1
            })
        }
    }

    // Italic: *text* (aber nicht innerhalb von ** oder Code)
    ITALIC_REGEX.lastIndex = 0
    while ((match = ITALIC_REGEX.exec(text)) !== null) {
        if (isInsideCode(match.index, match[0].length)) continue
        const isInsideBold = spans.some(s =>
            s.type === 'bold' && match.index >= s.start && match.index + match[0].length <= s.end
        )
        if (!isInsideBold) {
            spans.push({
                start: match.index,
                end: match.index + match[0].length,
                type: 'italic',
                markerLen: 1
            })
        }
    }

    // Underline: _text_ (aber nicht __ Doppel-Underscore, nicht in Code)
    UNDERLINE_REGEX.lastIndex = 0
    while ((match = UNDERLINE_REGEX.exec(text)) !== null) {
        if (isInsideCode(match.index, match[0].length)) continue
        spans.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'underline',
            markerLen: 1
        })
    }

    return spans
}

/**
 * Erstellt Mapping zwischen Data-Offsets (mit Markern) und Display-Offsets (ohne Marker).
 * @param {string} text
 * @param {Array<{start: number, end: number, type: string, markerLen: number}>} spans
 * @returns {{dataToDisplay: Int32Array, displayToData: number[], displayLength: number, markerPositions: Set<number>}}
 */
export function buildDisplayMapping(text, spans) {
    const markerPositions = collectMarkerPositions(spans)

    const dataToDisplay = new Int32Array(text.length)
    const displayToData = []

    let displayIdx = 0
    for (let dataIdx = 0; dataIdx < text.length; dataIdx++) {
        if (markerPositions.has(dataIdx)) {
            dataToDisplay[dataIdx] = -1
        } else {
            dataToDisplay[dataIdx] = displayIdx
            displayToData.push(dataIdx)
            displayIdx++
        }
    }

    return { dataToDisplay, displayToData, displayLength: displayIdx, markerPositions }
}

/**
 * Erkennt den Paragraph-Typ anhand des Textes.
 * @param {string} text
 * @returns {'heading'|'list'|'blockquote'|'hr'|'normal'}
 */
export function detectParagraphType(text) {
    const trimmed = text.trim()

    if (/^#{1,6}\s/.test(trimmed)) {
        return 'heading'
    }

    if (/^[-*]\s/.test(trimmed) || /^\d+(?:\.\d+)*\.\s/.test(trimmed)) {
        return 'list'
    }

    if (/^>\s/.test(trimmed)) {
        return 'blockquote'
    }

    if (/^-{3,}$/.test(trimmed)) {
        return 'hr'
    }

    return 'normal'
}

/**
 * Berechnet den Hanging Indent fuer Listen.
 * @param {string} text
 * @returns {number}
 */
export function getHangingIndent(text) {
    const match = text.match(/^(\s*)([-*]|\d+(?:\.\d+)*\.)\s/)
    return match ? match[0].length : 0
}

/**
 * Schneidet aus einem geparsten Zeichen-Array den Abschnitt fuer eine Zeile.
 * @param {Array<{char: string, style: string, dataOffset: number}>} fullParsed
 * @param {number} dataStart - Start Data-Offset der Zeile
 * @param {number} dataEnd - End Data-Offset der Zeile
 * @returns {Array<{char: string, style: string, dataOffset: number}>}
 */
export function sliceParsedForLine(fullParsed, dataStart, dataEnd) {
    let sliceStart = -1
    let sliceEnd = fullParsed.length

    for (let i = 0; i < fullParsed.length; i++) {
        if (sliceStart === -1 && fullParsed[i].dataOffset >= dataStart) {
            sliceStart = i
        }
        if (fullParsed[i].dataOffset >= dataEnd) {
            sliceEnd = i
            break
        }
    }

    if (sliceStart === -1) return []
    return fullParsed.slice(sliceStart, sliceEnd)
}

/**
 * Prueft ob ein Zeichen an einer Position ein verwaister Marker ist.
 * @param {string} text
 * @param {number} i - Position
 * @param {Set<number>} validMarkerPositions - Positionen die zu gueltigen Spans gehoeren
 * @returns {boolean}
 */
function isLikelyOrphanedMarker(text, i, validMarkerPositions) {
    if (validMarkerPositions.has(i)) return false

    const ch = text[i]

    if (ch === '*' || ch === '~') return true

    // _ ist speziell: `my_var` soll geschuetzt werden
    if (ch === '_') {
        const charBefore = i > 0 ? text[i - 1] : null
        const charAfter = i < text.length - 1 ? text[i + 1] : null
        if (charBefore && isLetterOrDigit(charBefore) && charAfter && isLetterOrDigit(charAfter)) {
            return false
        }
        return true
    }

    return false
}

/**
 * Findet die Wort-Grenzen an einer Position im Text.
 * @param {string} text
 * @param {number} offset
 * @returns {{start: number, end: number}|null}
 */
export function getWordBounds(text, offset) {
    if (offset < 0 || offset > text.length) return null



    if (offset < text.length && !isWordChar(text[offset])) {
        if (offset > 0 && isWordChar(text[offset - 1])) {
            offset--
        } else {
            return null
        }
    } else if (offset === text.length && offset > 0 && isWordChar(text[offset - 1])) {
        offset--
    } else if (offset === text.length) {
        return null
    }

    let start = offset
    while (start > 0 && isWordChar(text[start - 1])) {
        start--
    }

    let end = offset
    while (end < text.length && isWordChar(text[end])) {
        end++
    }

    if (start === end) return null
    return { start, end }
}

// ============================================================================
// Kategorie B: Parameter-angepasste Funktionen (this.xxx → Parameter)
// ============================================================================

/**
 * Konvertiert Text mit Markdown-Markern zu Zeichen-Array mit Styles.
 * Marker werden uebersprungen (WYSIWYG).
 * Optionale linkReplacements ersetzen Link-Marker durch Label-Zeichen mit Link-Style.
 *
 * @param {string} text
 * @param {string} baseStyle - Basis-Style (z.B. Style.EDITOR_TEXT, Style.CONSOLE_INPUT)
 * @param {{skipNewlines?: boolean}} [options] - skipNewlines=true fuer Console (ignoriert \n)
 * @param {Array<{dataStart: number, dataEnd: number, label: string, isBroken: boolean, targetId: number}>|null} [linkReplacements]
 * @returns {Array<{char: string, style: string, dataOffset: number, isLink?: boolean, linkTargetId?: number}>}
 */
export function parseToStyledChars(text, baseStyle, options, linkReplacements) {
    const result = []
    const skipNewlines = options && options.skipNewlines

    const spans = findFormatSpans(text)

    // Marker-Positionen sammeln (WYSIWYG: ueberspringen)
    const markerPositions = collectMarkerPositions(spans)

    // Link-Map aufbauen und Link-Marker-Positionen als uebersprungen markieren
    let linkMap = null
    if (linkReplacements && linkReplacements.length > 0) {
        linkMap = new Map()
        for (const link of linkReplacements) {
            linkMap.set(link.dataStart, link)
            for (let j = link.dataStart; j < link.dataEnd; j++) {
                markerPositions.add(j)
            }
        }
    }

    // Spans nach Content-Start sortieren für Cursor-basiertes Lookup O(n+s)
    const sortedSpans = spans.length > 0
        ? spans.slice().sort((a, b) => (a.start + a.markerLen) - (b.start + b.markerLen))
        : spans

    // Active-Spans-Tracking: welche Spans überdecken Position i?
    let nextSpanIdx = 0
    const activeSpans = []

    for (let i = 0; i < text.length; ) {
        // Link-Start? → Label-Zeichen mit Link-Style emittieren
        if (linkMap) {
            const link = linkMap.get(i)
            if (link) {
                const linkStyle = link.isBroken
                    ? Style.EDITOR_LINK_BROKEN
                    : Style.EDITOR_LINK
                for (let c = 0; c < link.label.length; c++) {
                    result.push({
                        char: link.label[c],
                        style: linkStyle,
                        dataOffset: link.dataStart,
                        isLink: true,
                        linkTargetId: link.targetId
                    })
                }
                i = link.dataEnd
                continue
            }
        }

        const cp = text.codePointAt(i)
        const ch = String.fromCodePoint(cp)

        if (skipNewlines && ch === '\n') { i += ch.length; continue }
        if (markerPositions.has(i)) { i++; continue }  // Marker sind immer BMP (length 1)

        // Abgelaufene Spans entfernen
        for (let a = activeSpans.length - 1; a >= 0; a--) {
            if (i >= activeSpans[a].end - activeSpans[a].markerLen) {
                activeSpans.splice(a, 1)
            }
        }

        // Neue Spans aktivieren (Content-Start <= i)
        while (nextSpanIdx < sortedSpans.length &&
               i >= sortedSpans[nextSpanIdx].start + sortedSpans[nextSpanIdx].markerLen) {
            const span = sortedSpans[nextSpanIdx]
            if (i < span.end - span.markerLen) {
                activeSpans.push(span)
            }
            nextSpanIdx++
        }

        // Formate aus aktiven Spans bestimmen (ohne Objekt-Allokation)
        let isBold = false, isItalic = false, isStrike = false, isUnderline = false, isCode = false
        for (let a = 0; a < activeSpans.length; a++) {
            const type = activeSpans[a].type
            if (type === 'bold') isBold = true
            else if (type === 'italic') isItalic = true
            else if (type === 'strikethrough') isStrike = true
            else if (type === 'underline') isUnderline = true
            else if (type === 'code') isCode = true
        }

        let style = baseStyle
        if (isCode) {
            // Inline-Code: Code-Style, basierend auf dem Kontext (Console vs. Editor)
            style = baseStyle.startsWith('CONSOLE_') ? Style.CONSOLE_AI_CODE : Style.EDITOR_CODE
        } else if (isBold && isItalic) {
            style = Style.EDITOR_BOLD_ITALIC
        } else if (isBold) {
            style = Style.EDITOR_BOLD
        } else if (isItalic) {
            style = Style.EDITOR_ITALIC
        }

        if (isStrike && isUnderline) {
            style = (style === baseStyle) ? Style.EDITOR_STRIKETHROUGH + '+UL' : style + '+ST+UL'
        } else if (isStrike) {
            style = (style === baseStyle) ? Style.EDITOR_STRIKETHROUGH : style + '+ST'
        } else if (isUnderline) {
            style = (style === baseStyle) ? Style.EDITOR_UNDERLINE : style + '+UL'
        }

        result.push({ char: ch, style, dataOffset: i })
        i += ch.length
    }

    return result
}

/**
 * Extrahiert den Text-String aus einem parsed-Array (vermeidet map().join()-Allokation).
 * @param {Array<{char: string}>} parsedChars
 * @returns {string}
 */
export function parsedCharsToString(parsedChars) {
    let s = ''
    for (let i = 0; i < parsedChars.length; i++) s += parsedChars[i].char
    return s
}

/**
 * Konvertiert das Ergebnis von parseToStyledChars() in formatRuns für computeStringLayout().
 * Gruppiert aufeinanderfolgende Zeichen mit gleichen Format-Flags.
 *
 * @param {Array<{char, style, dataOffset}>} parsedChars - Ergebnis von parseToStyledChars()
 * @returns {Array<{start: number, end: number, bold: boolean, italic: boolean, underline: boolean, strikethrough: boolean, style: string}>}
 */
export function parsedToFormatRuns(parsedChars) {
    if (!parsedChars || parsedChars.length === 0) return []

    const runs = []
    let runStart = 0
    let runStyle = parsedChars[0].style
    let runBold = _isBoldStyle(runStyle)
    let runItalic = _isItalicStyle(runStyle)
    let runUnderline = _isUnderlineStyle(runStyle)
    let runStrikethrough = _isStrikethroughStyle(runStyle)

    for (let i = 1; i < parsedChars.length; i++) {
        const style = parsedChars[i].style
        const bold = _isBoldStyle(style)
        const italic = _isItalicStyle(style)
        const underline = _isUnderlineStyle(style)
        const strikethrough = _isStrikethroughStyle(style)

        if (bold !== runBold || italic !== runItalic || underline !== runUnderline || strikethrough !== runStrikethrough || style !== runStyle) {
            runs.push({ start: runStart, end: i, bold: runBold, italic: runItalic, underline: runUnderline, strikethrough: runStrikethrough, style: runStyle })
            runStart = i
            runStyle = style
            runBold = bold
            runItalic = italic
            runUnderline = underline
            runStrikethrough = strikethrough
        }
    }

    // Letzten Run abschließen
    runs.push({ start: runStart, end: parsedChars.length, bold: runBold, italic: runItalic, underline: runUnderline, strikethrough: runStrikethrough, style: runStyle })

    return runs
}

function _isBoldStyle(style) {
    return style === Style.EDITOR_BOLD ||
           style === Style.EDITOR_BOLD_ITALIC ||
           (typeof style === 'string' && style.startsWith('EDITOR_BOLD'))
}

function _isItalicStyle(style) {
    return style === Style.EDITOR_ITALIC ||
           style === Style.EDITOR_BOLD_ITALIC ||
           (typeof style === 'string' && (style.startsWith('EDITOR_ITALIC') || style.startsWith('EDITOR_BOLD_ITALIC')))
}

function _isUnderlineStyle(style) {
    return typeof style === 'string' && (style === Style.EDITOR_UNDERLINE || style.includes('+UL') ||
           style === Style.EDITOR_LINK || style === Style.EDITOR_LINK_BROKEN || style === Style.CONSOLE_LINK)
}

function _isStrikethroughStyle(style) {
    return typeof style === 'string' && (style === Style.EDITOR_STRIKETHROUGH || style.startsWith(Style.EDITOR_STRIKETHROUGH) || style.includes('+ST') ||
           style === Style.EDITOR_LINK_BROKEN)
}

// ============================================================================
// Kategorie C: Reiner Kern extrahiert (Wrapper bleibt in Editor/Console)
// ============================================================================

/**
 * Fuegt Smart Blanks ein (verhindert Wort-Verkettung und Marker-Kollision).
 * @param {string} text
 * @param {number} cursorOffset
 * @param {number|null} insertStartOffset - Linke Grenze der Einfuegung
 * @returns {{newText: string, newCursorOffset: number}}
 */
export function ensureSmartBlanks(text, cursorOffset, insertStartOffset) {

    const isMarkerChar = (ch) => ch === '*' || ch === '~' || ch === '_'
    // Link-Marker (U+FFFC, Object Replacement Character) zaehlt wie ein Wort —
    // damit Smart Blanks Section-Links genauso von Nachbarwoertern trennen wie
    // ganze Woerter, inkl. Satzzeichen-Beruecksichtigung (kein Space vor '.').
    const isWordLike = (ch) => isWordChar(ch) || ch === '\uFFFC'

    const needsSpace = (left, right) => {
        if (!left || !right || left === ' ' || right === ' ') return false
        if (isWordLike(left) && isWordLike(right)) return true
        if (isWordLike(left) && isMarkerChar(right)) return true
        if (isMarkerChar(left) && isWordLike(right)) return true
        return false
    }

    let newText = text
    let newCursorOffset = cursorOffset
    // ops: Editier-Operationen für Span-Adjustment (in Ausführungsreihenfolge)
    // { type: 'insert', offset } oder { type: 'delete', offset }
    const ops = []

    // Space an rechter Grenze (Cursor)
    if (newCursorOffset > 0 && newCursorOffset < newText.length
        && needsSpace(newText[newCursorOffset - 1], newText[newCursorOffset])) {
        newText = newText.slice(0, newCursorOffset) + ' ' + newText.slice(newCursorOffset)
        ops.push({ type: 'insert', offset: newCursorOffset })
        newCursorOffset++
    }

    // Space an linker Grenze (Insert-Start)
    if (insertStartOffset != null && insertStartOffset > 0 && insertStartOffset < newText.length
        && needsSpace(newText[insertStartOffset - 1], newText[insertStartOffset])) {
        newText = newText.slice(0, insertStartOffset) + ' ' + newText.slice(insertStartOffset)
        ops.push({ type: 'insert', offset: insertStartOffset })
        newCursorOffset++
    }

    // Doppel-Space entfernen (am Cursor)
    if (newCursorOffset > 0 && newCursorOffset < newText.length
        && newText[newCursorOffset - 1] === ' ' && newText[newCursorOffset] === ' ') {
        newText = newText.slice(0, newCursorOffset) + newText.slice(newCursorOffset + 1)
        ops.push({ type: 'delete', offset: newCursorOffset })
    }

    // Doppel-Space entfernen (am Insert-Start)
    if (insertStartOffset != null && insertStartOffset > 0 && insertStartOffset < newText.length
        && newText[insertStartOffset - 1] === ' ' && newText[insertStartOffset] === ' ') {
        newText = newText.slice(0, insertStartOffset) + newText.slice(insertStartOffset + 1)
        ops.push({ type: 'delete', offset: insertStartOffset })
        if (newCursorOffset > insertStartOffset) {
            newCursorOffset--
        }
    }

    return { newText, newCursorOffset, ops }
}

/**
 * Entfernt leere Format-Marker-Paare (z.B. "****" → "").
 * @param {string} text
 * @param {number} cursorOffset
 * @returns {{newText: string, newCursorOffset: number}}
 */
export function cleanupEmptyFormatSpans(text, cursorOffset) {
    let workText = text
    let newOffset = cursorOffset

    let processing = true
    while (processing) {
        processing = false
        for (const pattern of [/\*\*\*\*/g, /~~~~/g, /__/g]) {
            pattern.lastIndex = 0
            const matches = []
            let m
            while ((m = pattern.exec(workText)) !== null) {
                matches.push({ start: m.index, len: m[0].length })
            }
            for (let i = matches.length - 1; i >= 0; i--) {
                const { start, len } = matches[i]
                workText = workText.slice(0, start) + workText.slice(start + len)
                if (start + len <= newOffset) {
                    newOffset -= len
                } else if (start < newOffset) {
                    newOffset -= (newOffset - start)
                }
                processing = true
            }
        }
    }

    if (workText === text) return { newText: text, newCursorOffset: cursorOffset }
    return { newText: workText, newCursorOffset: Math.max(0, Math.min(newOffset, workText.length)) }
}

/**
 * Rettet Multi-Wort-Format-Spans bei ungueltigem Boundary.
 * Wenn durch Backspace/Delete ein Space zwischen Wort und Format-Marker entfernt wurde,
 * repositioniert diese Funktion die Marker so, dass nur das angrenzende Wort
 * aus dem Format "rausfaellt" und der Rest formatiert bleibt.
 *
 * Bei Ein-Wort-Spans gibt sie null zurueck — dann greift repairOrphanedMarkers.
 *
 * @param {string} text
 * @param {number} cursorOffset
 * @returns {{newText: string, newCursorOffset: number}|null} - null wenn keine Rescue moeglich
 */
const RESCUE_PATTERNS = [
    { regex: /\*\*(.+?)\*\*/g, marker: '**', markerLen: 2 },
    { regex: /~~(.+?)~~/g, marker: '~~', markerLen: 2 },
    { regex: /(?<!\*)\*([^*]+?)\*(?!\*)/g, marker: '*', markerLen: 1 },
    { regex: /(?<!_)_([^_]+?)_(?!_)/g, marker: '_', markerLen: 1 }
]

// === URL-Erkennung ===

/**
 * Findet URL-Spans in einem Text (https:// und http://).
 * Gibt sortierte, nicht-ueberlappende Spans zurueck.
 *
 * @param {string} text
 * @returns {Array<{start: number, end: number, url: string}>}
 */
// Regex für URLs (Modul-Level, konsistent mit LINK_REGEX).
// \uFFFC ausgeschlossen: im Editor-Datenmodell steht der Section-Link-
// Platzhalter sonst mit in der URL, wenn ein Link direkt auf sie folgt.
const URL_REGEX = /https?:\/\/[^\s)\]>\uFFFC]+/g

export function findUrlSpans(text) {
    if (!text) return []

    URL_REGEX.lastIndex = 0
    const spans = []
    let match
    while ((match = URL_REGEX.exec(text)) !== null) {
        // Trailing-Interpunktion entfernen (., ,, ;, :, !) wenn nicht Teil der URL
        let url = match[0]
        while (url.length > 1 && /[.,;:!]$/.test(url)) {
            url = url.slice(0, -1)
        }
        spans.push({ start: match.index, end: match.index + url.length, url })
    }
    return spans
}

/**
 * Generische Hilfsfunktion: Findet den Span an einem Offset.
 * Erwartet sortierte Spans (nach start aufsteigend).
 *
 * @param {number} offset
 * @param {Array<{start: number, end: number}>} spans
 * @returns {{start: number, end: number}|null}
 */
function _getSpanAtOffset(offset, spans) {
    for (const span of spans) {
        if (offset >= span.start && offset < span.end) return span
        if (span.start > offset) break
    }
    return null
}

/**
 * Prueft ob ein Offset in einem URL-Span liegt.
 * Spans muessen sortiert sein (nach start).
 *
 * @param {number} offset
 * @param {Array<{start: number, end: number, url: string}>} spans
 * @returns {{start: number, end: number, url: string}|null}
 */
export function getUrlSpanAtOffset(offset, spans) {
    return _getSpanAtOffset(offset, spans)
}

// === Section Links ===

// Regex für Section-Links: [[@paragraphId]] — paragraphId darf Ziffern,
// Buchstaben, Unterstrich oder Bindestrich enthalten (UUIDs seit Multi-Projekt,
// und numerische Legacy-IDs aus Tests).
const LINK_REGEX = /\[\[@([\w-]+)\]\]/g

/**
 * Findet alle Section-Links im Text.
 * Links haben das Format [[@paragraphId]] — paragraphId ist UUID-String
 * (Multi-Projekt) oder Legacy-Integer (alte Tests/Daten). Bei rein
 * numerischer ID wird targetId als Number geliefert (Backwards-Compat),
 * sonst als String.
 *
 * @param {string} text
 * @returns {Array<{start: number, end: number, targetId: number|string}>}
 */
export function findLinkSpans(text) {
    if (!text) return []

    const spans = []
    LINK_REGEX.lastIndex = 0
    let match
    while ((match = LINK_REGEX.exec(text)) !== null) {
        const raw = match[1]
        const numeric = parseInt(raw, 10)
        const targetId = (Number.isFinite(numeric) && String(numeric) === raw)
            ? numeric : raw
        spans.push({
            start: match.index,
            end: match.index + match[0].length,
            targetId
        })
    }
    return spans
}

/**
 * Prueft ob ein @ an einer Position ein gueltiger Link-Trigger ist.
 * Gueltig wenn: am Textanfang, oder nach Blank/Satzzeichen.
 * Ungueltig wenn: mitten im Wort (z.B. Email-Adresse).
 *
 * @param {string} text - Der Text bis zur @-Position (exklusive)
 * @param {number} offset - Position des @
 * @returns {boolean}
 */
export function isLinkTriggerPosition(text, offset) {
    if (offset === 0) return true
    const prev = text[offset - 1]
    // Nach Blank oder Satzzeichen: gueltig
    return /[\s.,!?;:'"()\[\]\-–—]/.test(prev)
}

/**
 * Erweitert buildDisplayMapping um Link-Replacements.
 * Wenn linkReplacements vorhanden, werden Link-Marker durch Label-Text ersetzt.
 *
 * @param {string} text - Der Paragraph-Text
 * @param {Array} spans - Format-Spans
 * @param {Array<{dataStart: number, dataEnd: number, label: string}>|null} linkReplacements
 * @returns {{dataToDisplay: Int32Array, displayToData: number[], displayLength: number,
 *            markerPositions: Set<number>, displayText: string|null,
 *            linkDisplaySpans: Array<{displayStart: number, displayEnd: number, dataStart: number, dataEnd: number, targetId: number, isBroken: boolean}>|null}}
 */
export function buildDisplayMappingWithLinks(text, spans, linkReplacements) {
    if (!linkReplacements || linkReplacements.length === 0) {
        // Kein Link: Fallback auf Standard-Mapping
        const mapping = buildDisplayMapping(text, spans)
        return { ...mapping, displayText: null, linkDisplaySpans: null }
    }

    const markerPositions = collectMarkerPositions(spans)

    // Alle Link-Marker-Positionen ebenfalls als Marker markieren
    for (const link of linkReplacements) {
        for (let i = link.dataStart; i < link.dataEnd; i++) {
            markerPositions.add(i)
        }
    }

    // Link-Replacements nach Position sortieren (fuer sequentiellen Durchlauf)
    const sortedLinks = linkReplacements.slice().sort((a, b) => a.dataStart - b.dataStart)

    const dataToDisplay = new Int32Array(text.length)
    const displayToData = []
    const displayChars = []
    const linkDisplaySpans = []

    let displayIdx = 0
    let linkIdx = 0

    for (let dataIdx = 0; dataIdx < text.length; dataIdx++) {
        // Pruefen ob ein Link hier beginnt
        if (linkIdx < sortedLinks.length && dataIdx === sortedLinks[linkIdx].dataStart) {
            const link = sortedLinks[linkIdx]
            const label = link.label

            // Alle Data-Positionen des Link-Markers → Display-Ende des Labels
            // (Cursor-Snap: jede Position im Marker verweist auf nach dem Label)
            const labelDisplayStart = displayIdx
            for (let d = link.dataStart; d < link.dataEnd; d++) {
                dataToDisplay[d] = labelDisplayStart + label.length  // nach dem Label
            }

            // Label-Zeichen als virtuelle Display-Positionen einfuegen
            // Erstes Label-Zeichen → dataStart (damit Word-Wrap den Link als Ganzes umbrechen kann)
            // Restliche Label-Zeichen → dataEnd (Cursor nach dem Link)
            // Leerzeichen durch Non-Breaking Space ersetzen → Link ist atomare Einheit im Word-Wrap
            for (let c = 0; c < label.length; c++) {
                displayToData.push(c === 0 ? link.dataStart : link.dataEnd)
                displayChars.push(label[c] === ' ' ? '\u00A0' : label[c])
                displayIdx++
            }

            // Link-Display-Span merken
            linkDisplaySpans.push({
                displayStart: labelDisplayStart,
                displayEnd: labelDisplayStart + label.length,
                dataStart: link.dataStart,
                dataEnd: link.dataEnd,
                targetId: link.targetId,
                isBroken: link.isBroken || false
            })

            // Data-Index zum Ende des Links vorspulen (die for-Schleife inkrementiert nochmal)
            dataIdx = link.dataEnd - 1
            linkIdx++
            continue
        }

        // Normaler Format-Marker → ausblenden
        if (markerPositions.has(dataIdx)) {
            dataToDisplay[dataIdx] = -1
            continue
        }

        // Normales Zeichen
        dataToDisplay[dataIdx] = displayIdx
        displayToData.push(dataIdx)
        displayChars.push(text[dataIdx])
        displayIdx++
    }

    return {
        dataToDisplay,
        displayToData,
        displayLength: displayIdx,
        markerPositions,
        displayText: displayChars.join(''),
        linkDisplaySpans
    }
}

/**
 * Duenner Wrapper um parseToStyledChars mit Link-Replacements.
 * Link-Marker werden durch Label-Zeichen mit EDITOR_LINK Style ersetzt.
 *
 * @param {string} text
 * @param {string} baseStyle
 * @param {Array<{dataStart: number, dataEnd: number, label: string, isBroken: boolean, targetId: number}>|null} linkReplacements
 * @param {object} [options]
 * @returns {Array<{char: string, style: string, dataOffset: number, isLink?: boolean, linkTargetId?: number}>}
 */
export function parseToStyledCharsWithLinks(text, baseStyle, linkReplacements, options) {
    return parseToStyledChars(text, baseStyle, options, linkReplacements)
}
